import GoogleAnalyticsEvent from 'dataTransferObjects/googleAnalyticsEvent';
import GoogleAnalyticsCustomDimensions from 'constants/googleAnalyticsCustomDimension';
import { createMarkerHashedKey } from 'libraries/mapLibrary';
import { BasicCommunityCard, PaidCommunityCard, PaidMasterPlannedCommunityCard } from 'features/standardCard';
import { markerTipSvgHtml } from '../constants/svgIcons';
import MarkerType from '../enums/markerType';
import { isHousingDevelopmentViewed } from '../services/markerSessionService';
import getClassesToApply from '../services/unitMarkerService';

/* Creates a new instance of a housing development that will be plotted in the map as a marker.
 */
export default class HousingDevelopmentMarker {
  constructor(housing) {
    this.geoId = housing.GeoId;
    this.type = MarkerType.HousingDevelopment.toString();
    this.id = housing.Id;
    this.communityId = housing.Id;
    this.latitude = housing.Latitude;
    this.longitude = housing.Longitude;
    this.priceText = housing.PriceText;
    this.shortPrice = housing.ShortPrice;
    this.title = housing.Title;
    this.totalPlans = housing.TotalPlans;
    this.priceReducedText = housing.PriceReducedText;
    this.priceReducedTrackingLabel = housing.PriceReducedTrackingLabel;
    this.communityName = housing.Title;
    this.builderId = housing.BuilderId;
    this.pageNumber = housing.Page;
    this.priceLow = housing.PriceLow;
    this.priceHigh = housing.PriceHigh;
    this.hasInteractiveSitePlan = housing.HasInteractiveSitePlan;
    this.sitePlanCenterLatitude = housing.SitePlanCenterLatitude;
    this.sitePlanCenterLongitude = housing.SitePlanCenterLongitude;
    this.isViewed = isHousingDevelopmentViewed(housing.Id);
    this.isBasic = housing.IsBasic === 1;
    this.isFeaturedListing = housing.IsFeaturedListing || false;
    this.isMasterPlannedCommunity = housing.IsMasterPlannedCommunity || false;
    this.isAdult = housing.IsAdult || false;
    this.isCallForPrice = housing.IsCallForPrice || false;
    this.thumbnail = housing.Thumbnail;
    this.bedrooms = housing.Bedrooms;
    this.bathrooms = housing.Bathrooms;
    this.address = housing.Address;
    this.brandName = housing.BrandName;
    this.squareFeet = housing.SquareFeetRange;
    this.builderCount = housing.BuilderCount;
    this.communityCount = housing.CommunityCount;
    this.city = housing.City;
    this.stateAbbreviation = housing.StateAbbreviation;
    this.zip = housing.Zip;
    this.communityStatus = housing.CommunityStatus;
    this.averageRating = housing.AverageRating;
    this.previousPrice = housing.PreviousPrice;
    this.isHotDeal = housing.IsHotDeal;

    if (this.isBasic) {
      this.zIndex = 1;
    } else if (this.isFeaturedListing) {
      this.zIndex = 3;
    } else {
      this.zIndex = 2;
    }
  }

  /**
   * getMarkerText: gets the text the marker is going to show once rendered.
   * @returns a text to show on the map once rendered.
   */
  getMarkerText() {
    if (!this.isCallForPrice) {
      return this.shortPrice;
    }
    return '';
  }

  /**
   * @returns The name of the extend marker type.
   */
  getType() {
    return this.type;
  }

  /**
   * It returns a string that is the url to the detail page of the community
   * @returns The string `/communitydetail/community-${this.id}`
   */
  getDetailUrl() {
    return `/communitydetail/builder-${this.builderId}/community-${this.id}`;
  }

  /**
   * This function returns a new GoogleAnalyticsEvent object with the event category, event action, and
   * event label set.
   * @returns A new instance of the GoogleAnalyticsEvent class.
   */
  getGoogleAnalyticsEventForClicks() {
    const customDimensions = {
      [GoogleAnalyticsCustomDimensions.PriceReduction().getName()]:
        this.priceReducedTrackingLabel,
      [GoogleAnalyticsCustomDimensions.CommunityName().getName()]:
        this.communityName,
      [GoogleAnalyticsCustomDimensions.CommunityId().getName()]: this.id,
      [GoogleAnalyticsCustomDimensions.IsPaid().getName()]: !this.isBasic
    };

    const url = this.getDetailUrl();
    return new GoogleAnalyticsEvent(
      'Map-Pin-Clicks',
      'Communities: SRP-Map-Comm-Clicks',
      url,
      customDimensions
    );
  }

  /**
   * This function returns a new meta data object with the required information based on the map pin data.
   * @returns A new instance of the meta data object.
   */
  getHousingLocationMetaData() {
    return {
      priceReducedTrackingLabel: this.priceReducedTrackingLabel,
      communityName: this.communityName,
      communityId: this.id,
      isPaid: !this.isBasic,
      priceLow: this.priceLow,
      priceHigh: this.priceHigh,
      hasInteractiveSitePlan: this.hasInteractiveSitePlan,
      id: this.id,
      isMasterPlannedCommunity: this.isMasterPlannedCommunity,
      thumbnail: this.thumbnail,
      priceText: this.priceText,
      bedrooms: this.bedrooms,
      bathrooms: this.bathrooms,
      address: this.address,
      brandName: this.brandName,
      squareFeet: this.squareFeet,
      url: this.getDetailUrl(),
      builderId: this.builderId,
      isFeaturedListing: this.isFeaturedListing,
      builderCount: this.builderCount,
      communityCount: this.communityCount,
      city: this.city,
      stateAbbreviation: this.stateAbbreviation,
      zip: this.zip,
      sitePlanCenterLatitude: this.sitePlanCenterLatitude,
      sitePlanCenterLongitude: this.sitePlanCenterLongitude,
      communityStatus: this.communityStatus,
      isAdult: this.isAdult,
      averageRating: this.averageRating,
      previousPrice: this.previousPrice,
      isHotDeal: this.isHotDeal,
    };
  }

  /**
   * This function returns the HTML in charge of build the hover card of a given marker
   * @returns hover card HTML
   */
  getCard() {
    if (this.isMasterPlannedCommunity) {
      return new PaidMasterPlannedCommunityCard();
    }

    if (this.isBasic) {
      return new BasicCommunityCard();
    }

    return new PaidCommunityCard();
  }

  /**
   * @returns {string} The complete HTML that represents the housing development marker
   * as an icon and a card with details.
   */
  getIcon() {
    const card = this.getCard();
    card.setMetadata(this.getHousingLocationMetaData());
    return `
      <div data-marker-content class="nhs-c-advance-marker nhs-c-tooltip-hide">
        ${this.getHtmlPinElement()}
        ${card.getHtml()}
      </div>
    `;
  }

  /**
   * getShortPriceIconSection: gets a div with the short price to show in the marker icon.
   * @returns a div with the short price to show in the marker icon.
   */
  getShortPriceIconSection() {
    return this.getMarkerText()
      ? `<div data-marker-text data-qa="marker_price" class="nhs-c-marker__text">${this.getMarkerText()}</div>`
      : '';
  }

  /**
   * getDescription: returns a description that will help screen readers
   * provide a better explanation about the marker itself.
   * @returns a description for the marker.
   */
  getDescription() {
    if (this.shortPrice) {
      return `Homes from ${this.shortPrice}`;
    }

    return this.title;
  }

  /**
   * getMarkerStyles function is in charge of returning an array with the classes to use for the map marker.
   * @returns an array representing all the classes we should apply to the map marker in terms of its characteristics.
   */
  getMarkerStyles() {
    const { base } = getClassesToApply({
      isMasterPlannedCommunity: this.isMasterPlannedCommunity,
      isSpotlight: this.isFeaturedListing,
      isAdult: this.isAdult,
      isViewed: this.isViewed
    });

    const classesToApply = [...base].filter(Boolean);
    return classesToApply;
  }

  /**
   * Returns the HTML string representing a pin marker of the housing development.
   * @returns {string} The HTML string for the marker pin.
   */
  getHtmlPinElement() {
    const markerStyles = this.getMarkerStyles().join(' ');
    const dataAttributes = [
      `data-marker-icon="true"`,
      `class="${markerStyles}"`,
      `data-id="${this.id}"`,
      `data-type="${this.getType()}"`,
      `data-geographic-key="${createMarkerHashedKey(
        this.latitude,
        this.longitude
      )}"`,
      `data-price-reduced-tracking-label="${this.priceReducedTrackingLabel}"`,
      `data-is-spotlight="${this.isFeaturedListing}"`,
      `data-is-mpc="${!!this.isMasterPlannedCommunity}"`,
      `data-is-adult="${!!this.isAdult}"`,
      `data-is-no-price="${!!this.isCallForPrice}"`,
      `data-viewed="${!!this.isViewed}"`,
      `data-is-price-reduction="${!!this.previousPrice}"`,
      `data-qa="marker_container"`,
      this.isBasic ? `data-is-basic="true"` : `data-is-paid="true"`
    ].join(' ');

    if (!this.shortPrice || this.isCallForPrice) {
      return `<div ${dataAttributes}></div>`;
    }

    return `
      <div ${dataAttributes}>
        <div class="nhs-c-marker">
          <div class="nhs-c-marker__box">
            <div data-marker-text data-qa="marker_price" class="nhs-c-marker__text">${this.shortPrice}</div>          
            <div class="nhs-c-marker__arrow--tip">${markerTipSvgHtml}</div>
          </div>
        </div>
      </div>
    `;
  }
}
